<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

const props = defineProps({
  campaignId: {
    type: [String, Number],
    required: true,
  },
  accountId: {
    type: [String, Number],
    required: true,
  },
});

const http = window.axios;

const { t } = useI18n();

const logs = ref([]);
const meta = ref({ total: 0, page: 1, total_pages: 1 });
const isLoading = ref(false);
const isBulkActionLoading = ref(false);
const redispatchingIds = ref(new Set());
const removingContactIds = ref(new Set());
const markingDispatchedIds = ref(new Set());
const selectedLogIds = ref([]);
const detailDialogRef = ref(null);
const removeDialogRef = ref(null);
const selectedLog = ref(null);
const pendingRemovalLog = ref(null);
let latestFetchRequestId = 0;

// Filters, ordering, page size and cross-page "select all".
const statusFilter = ref('all');
const sortOrder = ref('schedule');
const perPage = ref(50);
const selectAllMatching = ref(false);

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'not_dispatched', label: 'Não disparados' },
  { value: 'dispatched', label: 'Disparados' },
  { value: 'failed', label: 'Com erro' },
  { value: 'cancelled', label: 'Cancelados' },
];
const SORT_OPTIONS = [
  { value: 'schedule', label: 'Próximos a disparar' },
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigos' },
];
const PER_PAGE_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
];

const statusColorMap = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  scheduled: 'bg-n-blue-3 text-n-blue-11',
  sending: 'bg-n-blue-3 text-n-blue-11',
  sent: 'bg-n-teal-3 text-n-teal-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  cancelled: 'bg-n-slate-3 text-n-slate-10',
  paused: 'bg-n-slate-5 text-n-slate-12',
};

const STATUS_KEYS = {
  pending: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_PENDING',
  scheduled: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_SCHEDULED',
  sending: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_SENDING',
  sent: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_SENT',
  failed: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_FAILED',
  cancelled: 'CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS_CANCELLED',
};

const statusLabel = status => {
  if (status === 'paused') return 'Pausado';
  const key = STATUS_KEYS[status] || STATUS_KEYS.pending;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(key);
};

const apiUrl = computed(
  () =>
    `/api/v1/accounts/${props.accountId}/campaigns/${props.campaignId}/dispatch_logs`
);

const resetViewerState = () => {
  logs.value = [];
  meta.value = { total: 0, page: 1, total_pages: 1 };
  selectedLogIds.value = [];
  selectedLog.value = null;
  pendingRemovalLog.value = null;
};

const fetchLogs = async (page = 1) => {
  latestFetchRequestId += 1;
  const requestId = latestFetchRequestId;
  isLoading.value = true;

  try {
    const { data } = await http.get(apiUrl.value, {
      params: {
        page,
        per: perPage.value,
        status: statusFilter.value,
        sort: sortOrder.value,
        _t: Date.now(),
      },
    });

    if (requestId !== latestFetchRequestId) {
      return;
    }

    const nextLogs = data.data || [];

    logs.value = nextLogs;
    meta.value = data.meta || { total: nextLogs.length, page, total_pages: 1 };
    selectedLogIds.value = selectedLogIds.value.filter(id =>
      nextLogs.some(log => log.id === id)
    );

    if (selectedLog.value) {
      selectedLog.value =
        nextLogs.find(log => log.id === selectedLog.value.id) || null;
    }

    if (pendingRemovalLog.value) {
      pendingRemovalLog.value =
        nextLogs.find(log => log.id === pendingRemovalLog.value.id) || null;
    }
  } catch {
    if (requestId !== latestFetchRequestId) {
      return;
    }

    resetViewerState();
  } finally {
    if (requestId === latestFetchRequestId) {
      isLoading.value = false;
    }
  }
};

const handleRedispatch = async log => {
  redispatchingIds.value.add(log.id);
  try {
    const { data } = await http.post(`${apiUrl.value}/${log.id}/redispatch`);
    const index = logs.value.findIndex(l => l.id === log.id);
    if (index > -1 && data.data) {
      logs.value[index] = data.data;
    }
  } finally {
    redispatchingIds.value.delete(log.id);
  }
};

// Mark a queued row as manually dispatched so reactivating the queue never
// re-sends it. Backend flips it to 'sent' + tags it manual.
const handleMarkDispatched = async log => {
  markingDispatchedIds.value.add(log.id);
  try {
    const { data } = await http.post(
      `${apiUrl.value}/${log.id}/mark_dispatched`
    );
    const index = logs.value.findIndex(l => l.id === log.id);
    if (index > -1 && data.data) {
      logs.value[index] = data.data;
    }
    useAlert('Marcado como concluído');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Não foi possível marcar como concluído'
    );
  } finally {
    markingDispatchedIds.value.delete(log.id);
  }
};

const requestRemoveContact = log => {
  pendingRemovalLog.value = log;
  removeDialogRef.value?.open();
};

function closeRemoveDialog() {
  pendingRemovalLog.value = null;
}

function closeDetails() {
  selectedLog.value = null;
}

const handleRemoveContact = async () => {
  const log = pendingRemovalLog.value;

  if (!log) {
    return;
  }

  removingContactIds.value.add(log.id);

  try {
    const { data } = await http.post(
      `${apiUrl.value}/${log.id}/cancel_contact`
    );
    useAlert(
      t('CAMPAIGN.ENHANCED.DISPATCH_LOG.REMOVE_CONTACT_SUCCESS', {
        count: data.meta?.cancelled_count || 0,
      })
    );
    await fetchLogs(meta.value.page);
    if (selectedLog.value?.id === log.id) {
      closeDetails();
    }
    closeRemoveDialog();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.ENHANCED.DISPATCH_LOG.REMOVE_CONTACT_ERROR')
    );
  } finally {
    removingContactIds.value.delete(log.id);
  }
};

const canRedispatch = log => log.status !== 'sending';
const canRemoveContactDispatch = log =>
  ['pending', 'scheduled'].includes(log.status);
const canMarkDispatched = log =>
  ['pending', 'scheduled', 'paused'].includes(log.status);

// A manually-marked 'sent' log reads as "Concluído (manual)" to distinguish it
// from a real system dispatch.
const rowStatusLabel = log =>
  log.status === 'sent' && log.metadata?.manual_dispatch
    ? 'Concluído (manual)'
    : statusLabel(log.status);
const selectedLogs = computed(() =>
  logs.value.filter(log => selectedLogIds.value.includes(log.id))
);
const allVisibleSelected = computed(
  () =>
    logs.value.length > 0 && selectedLogIds.value.length === logs.value.length
);
const hasSelectedLogs = computed(
  () => selectedLogIds.value.length > 0 || selectAllMatching.value
);
// Across pages we cannot inspect each row, so rely on the backend to filter to
// eligible logs; enable the actions whenever "select all" is active.
const canBulkCancel = computed(
  () =>
    selectAllMatching.value ||
    selectedLogs.value.some(log =>
      ['pending', 'scheduled'].includes(log.status)
    )
);
const canBulkRedispatch = computed(
  () =>
    selectAllMatching.value || selectedLogs.value.some(log => canRedispatch(log))
);
const canBulkMarkDispatched = computed(
  () =>
    selectAllMatching.value ||
    selectedLogs.value.some(log => canMarkDispatched(log))
);

const toggleLogSelection = logId => {
  if (selectedLogIds.value.includes(logId)) {
    selectedLogIds.value = selectedLogIds.value.filter(id => id !== logId);
    return;
  }

  selectedLogIds.value = [...selectedLogIds.value, logId];
};

const toggleSelectAll = () => {
  if (allVisibleSelected.value || selectAllMatching.value) {
    selectedLogIds.value = [];
    selectAllMatching.value = false;
    return;
  }

  selectedLogIds.value = logs.value.map(log => log.id);
};

// Cross-page selection: acts on every log matching the current filter.
const selectAllAcrossPages = () => {
  selectAllMatching.value = true;
  selectedLogIds.value = logs.value.map(log => log.id);
};

const clearSelection = () => {
  selectedLogIds.value = [];
  selectAllMatching.value = false;
};

const selectedCount = computed(() =>
  selectAllMatching.value ? meta.value.total : selectedLogIds.value.length
);

// Show the "select all N" prompt when the whole page is ticked but more pages exist.
const canSelectAllAcrossPages = computed(
  () =>
    !selectAllMatching.value &&
    allVisibleSelected.value &&
    meta.value.total > logs.value.length
);

const applyLogControls = () => {
  clearSelection();
  fetchLogs(1);
};

// SelectMenu shows the active option's label as its button text, so derive it.
const statusFilterLabel = computed(
  () =>
    STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter.value)?.label ??
    STATUS_FILTER_OPTIONS[0].label
);
const sortOrderLabel = computed(
  () =>
    SORT_OPTIONS.find(o => o.value === sortOrder.value)?.label ??
    SORT_OPTIONS[0].label
);
// SelectMenu's modelValue is a String; perPage is numeric, so bridge both ways.
const perPageValue = computed(() => String(perPage.value));

const handleStatusChange = value => {
  statusFilter.value = value;
  applyLogControls();
};
const handleSortChange = value => {
  sortOrder.value = value;
  applyLogControls();
};
const handlePerPageChange = value => {
  perPage.value = Number(value);
  applyLogControls();
};

const bulkSuccessMessage = (endpoint, count) => {
  if (endpoint === 'bulk_cancel') {
    return t('CAMPAIGN.ENHANCED.DISPATCH_LOG.BULK_CANCEL_SUCCESS', {
      count,
    });
  }

  if (endpoint === 'bulk_remove') {
    return t('CAMPAIGN.ENHANCED.DISPATCH_LOG.BULK_REMOVE_SUCCESS', {
      count,
    });
  }

  if (endpoint === 'bulk_mark_dispatched') {
    return `${count} marcado(s) como concluído(s)`;
  }

  return t('CAMPAIGN.ENHANCED.DISPATCH_LOG.BULK_REDISPATCH_SUCCESS', {
    count,
  });
};

const runBulkAction = async ({ endpoint, countKey }) => {
  if (!selectedLogIds.value.length && !selectAllMatching.value) {
    return;
  }

  isBulkActionLoading.value = true;

  try {
    const payload = selectAllMatching.value
      ? { select_all: true, status: statusFilter.value }
      : { log_ids: selectedLogIds.value };
    const { data } = await http.post(`${apiUrl.value}/${endpoint}`, payload);

    useAlert(bulkSuccessMessage(endpoint, data.meta?.[countKey] || 0));

    clearSelection();
    await fetchLogs(1);
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.ENHANCED.DISPATCH_LOG.BULK_ACTION_ERROR')
    );
  } finally {
    isBulkActionLoading.value = false;
  }
};

const handleBulkCancel = async () => {
  await runBulkAction({
    endpoint: 'bulk_cancel',
    countKey: 'cancelled_count',
  });
};

const handleBulkRemove = async () => {
  await runBulkAction({
    endpoint: 'bulk_remove',
    countKey: 'removed_count',
  });
};

const handleBulkRedispatch = async () => {
  await runBulkAction({
    endpoint: 'bulk_redispatch',
    countKey: 'redispatched_count',
  });
};

const handleBulkMarkDispatched = async () => {
  await runBulkAction({
    endpoint: 'bulk_mark_dispatched',
    countKey: 'marked_count',
  });
};

const openDetails = log => {
  selectedLog.value = log;
  detailDialogRef.value?.open();
};

// --- Manual WhatsApp (wa.me) shortcuts -------------------------------------
// Lets the operator open the contact on WhatsApp Web/app to send by hand,
// either blank or pre-filled with the campaign message.
const waDigits = phone => (phone || '').replace(/\D/g, '');
const hasWhatsapp = log => waDigits(log.contact_phone).length >= 8;

// Best available message text for a row: prefer the final/rendered content,
// fall back to what was (or would be) sent.
const logMessageText = log =>
  log?.metadata?.final_content ||
  log?.metadata?.rendered_content ||
  log?.content_sent ||
  '';

const openWhatsapp = (log, withMessage) => {
  const digits = waDigits(log.contact_phone);
  if (!digits) return;

  let url = `https://wa.me/${digits}`;
  if (withMessage) {
    const text = logMessageText(log);
    if (text) {
      url += `?text=${encodeURIComponent(text)}`;
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('pt-BR');
};

const formatValue = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  if (typeof value === 'boolean') {
    return value
      ? t('CAMPAIGN.ENHANCED.DISPATCH_LOG.YES')
      : t('CAMPAIGN.ENHANCED.DISPATCH_LOG.NO');
  }

  return String(value);
};

const formatJson = value => {
  if (!value || (typeof value === 'object' && !Object.keys(value).length)) {
    return '-';
  }

  if (typeof value === 'string') {
    return value;
  }

  return JSON.stringify(value, null, 2);
};

const formatAudienceSourceType = sourceType => {
  const labels = {
    group: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.SOURCE_TYPES.GROUP'),
    direct_group: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.SOURCE_TYPES.DIRECT_GROUP'),
    contact: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.SOURCE_TYPES.CONTACT'),
    label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.SOURCE_TYPES.LABEL'),
    imported_contact: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.SOURCE_TYPES.IMPORTED'),
  };

  return labels[sourceType] || sourceType || '-';
};

const formatSourceGroups = groups => {
  if (!Array.isArray(groups) || !groups.length) {
    return '-';
  }

  return groups
    .map(group => group.group_name || group.group_identifier)
    .filter(Boolean)
    .join(', ');
};

const formatSourceSummary = log => {
  if (!log?.source_groups?.length) {
    return '-';
  }

  return `${formatAudienceSourceType(log.audience_source_type)}: ${formatSourceGroups(
    log.source_groups
  )}`;
};

const selectedMetadata = computed(() => selectedLog.value?.metadata || {});
const removeDialogLoading = computed(() =>
  removingContactIds.value.has(pendingRemovalLog.value?.id)
);

const detailItems = computed(() => {
  if (!selectedLog.value) {
    return [];
  }

  return [
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.CONTACT'),
      value: selectedLog.value.contact_name,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.PHONE'),
      value: selectedLog.value.contact_phone,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.EMAIL'),
      value: selectedLog.value.contact_email,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.STATUS'),
      value: statusLabel(selectedLog.value.status),
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.DISPATCH_INBOX'),
      value: selectedLog.value.dispatch_inbox_name,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.AUDIENCE_SOURCE'),
      value: formatAudienceSourceType(selectedLog.value.audience_source_type),
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.SOURCE_GROUPS'),
      value: formatSourceGroups(selectedLog.value.source_groups),
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.MESSAGE_INDEX'),
      value: selectedLog.value.message_index + 1,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.ORIGINAL_CONTENT'),
      value: selectedMetadata.value.original_content,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.RENDERED_CONTENT'),
      value: selectedMetadata.value.rendered_content,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.FINAL_CONTENT'),
      value:
        selectedMetadata.value.final_content || selectedLog.value.content_sent,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.FILE_NAME'),
      value: selectedLog.value.file_name,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.MESSAGE_TYPE'),
      value: selectedMetadata.value.message_type,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.AI_ENABLED'),
      value: selectedMetadata.value.captain_enabled,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.AI_PROMPT'),
      value: selectedMetadata.value.captain_prompt,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.AUDIO_SEPARATE'),
      value: selectedMetadata.value.send_text_separately,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.SENT_MESSAGE_IDS'),
      value: selectedMetadata.value.sent_message_ids,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.HTTP_STATUS'),
      value: selectedLog.value.http_status_code,
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.SCHEDULED_FOR'),
      value: formatDate(selectedLog.value.scheduled_for),
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.DISPATCHED_AT'),
      value: formatDate(selectedLog.value.dispatched_at),
    },
    {
      label: t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.ERROR'),
      value: selectedLog.value.error_message,
    },
  ];
});

watch(
  () => [props.accountId, props.campaignId],
  ([nextAccountId, nextCampaignId]) => {
    latestFetchRequestId += 1;

    if (!nextAccountId || !nextCampaignId) {
      isLoading.value = false;
      resetViewerState();
      return;
    }

    resetViewerState();
    fetchLogs(1);
  },
  { immediate: true }
);
</script>

<template>
  <div class="space-y-4">
    <!-- Toolbar: filters + count + refresh + pagination -->
    <div
      class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl border border-n-slate-4 bg-n-solid-1 px-4 py-3"
    >
      <div class="flex flex-wrap items-center gap-x-5 gap-y-3">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-slate-11">Status</span>
          <SelectMenu
            :model-value="statusFilter"
            :options="STATUS_FILTER_OPTIONS"
            :label="statusFilterLabel"
            sub-menu-position="bottom"
            @update:model-value="handleStatusChange"
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-slate-11">Ordenar</span>
          <SelectMenu
            :model-value="sortOrder"
            :options="SORT_OPTIONS"
            :label="sortOrderLabel"
            sub-menu-position="bottom"
            @update:model-value="handleSortChange"
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-slate-11">Por página</span>
          <SelectMenu
            :model-value="perPageValue"
            :options="PER_PAGE_OPTIONS"
            :label="perPageValue"
            sub-menu-position="bottom"
            @update:model-value="handlePerPageChange"
          />
        </div>

        <span
          class="text-xs font-medium tabular-nums text-n-slate-11 ltr:border-l rtl:border-r border-n-slate-4 ltr:pl-5 rtl:pr-5"
        >
          {{ meta.total }} no total
        </span>
      </div>

      <div class="flex items-center gap-2">
        <div
          v-if="meta.total_pages > 1"
          class="flex items-center gap-1 rounded-lg bg-n-alpha-black2 p-1"
        >
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-black1 disabled:opacity-40 disabled:hover:bg-transparent"
            :disabled="meta.page <= 1 || isLoading"
            aria-label="Página anterior"
            @click="fetchLogs(meta.page - 1)"
          >
            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M12 5l-5 5 5 5" /></svg>
          </button>
          <span class="px-1 text-xs font-medium tabular-nums text-n-slate-11">
            {{ meta.page }} / {{ meta.total_pages }}
          </span>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-black1 disabled:opacity-40 disabled:hover:bg-transparent"
            :disabled="meta.page >= meta.total_pages || isLoading"
            aria-label="Próxima página"
            @click="fetchLogs(meta.page + 1)"
          >
            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.75"><path d="M8 5l5 5-5 5" /></svg>
          </button>
        </div>
        <Button
          variant="faded"
          size="sm"
          color="slate"
          type="button"
          icon="i-lucide-refresh-cw"
          :is-loading="isLoading"
          aria-label="Atualizar"
          @click="fetchLogs(meta.page)"
        />
      </div>
    </div>

    <!-- Contextual bulk-action bar: only visible when rows are selected -->
    <div
      v-if="hasSelectedLogs"
      class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-blue-6 bg-n-blue-2 px-4 py-2.5"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span class="font-semibold tabular-nums text-n-blue-12">
          {{ selectedCount }} selecionado(s)
        </span>
        <button
          v-if="canSelectAllAcrossPages"
          type="button"
          class="rounded px-1 font-medium text-n-blue-11 underline-offset-2 hover:underline"
          @click="selectAllAcrossPages"
        >
          Selecionar todos os {{ meta.total }}
        </button>
        <button
          type="button"
          class="rounded px-1 text-n-slate-11 underline-offset-2 hover:underline"
          @click="clearSelection"
        >
          Limpar
        </button>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          color="amber"
          type="button"
          icon="i-lucide-ban"
          :label="t('CAMPAIGN.ENHANCED.BUTTONS.BULK_CANCEL')"
          :disabled="!canBulkCancel || isBulkActionLoading"
          :is-loading="isBulkActionLoading"
          @click="handleBulkCancel"
        />
        <Button
          variant="outline"
          size="sm"
          color="teal"
          type="button"
          icon="i-lucide-send"
          :label="t('CAMPAIGN.ENHANCED.BUTTONS.BULK_REDISPATCH')"
          :disabled="!canBulkRedispatch || isBulkActionLoading"
          :is-loading="isBulkActionLoading"
          @click="handleBulkRedispatch"
        />
        <Button
          variant="outline"
          size="sm"
          color="teal"
          type="button"
          icon="i-lucide-check-check"
          label="Marcar concluído"
          :disabled="!canBulkMarkDispatched || isBulkActionLoading"
          :is-loading="isBulkActionLoading"
          @click="handleBulkMarkDispatched"
        />
        <Button
          variant="outline"
          size="sm"
          color="ruby"
          type="button"
          icon="i-lucide-trash-2"
          :label="t('CAMPAIGN.ENHANCED.BUTTONS.BULK_REMOVE')"
          :disabled="isBulkActionLoading"
          :is-loading="isBulkActionLoading"
          @click="handleBulkRemove"
        />
      </div>
    </div>

    <div
      v-if="!logs.length && !isLoading"
      class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-n-slate-5 py-14 text-center"
    >
      <svg
        class="h-8 w-8 text-n-slate-8"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
      <p class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.NO_LOGS') }}
      </p>
    </div>

    <div
      v-if="logs.length"
      class="overflow-x-auto rounded-xl border border-n-slate-4"
    >
      <table class="w-full text-sm">
        <thead>
          <tr
            class="border-b border-n-slate-5 bg-n-solid-1 text-[11px] uppercase tracking-wide text-n-slate-10"
          >
            <th class="w-10 px-3 py-2.5 text-left font-semibold">
              <input
                type="checkbox"
                class="h-4 w-4 cursor-pointer rounded border-n-slate-6"
                :checked="allVisibleSelected || selectAllMatching"
                @change="toggleSelectAll"
              />
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.CONTACT') }}
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.PHONE') }}
            </th>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <th class="px-3 py-2.5 text-left font-semibold">#</th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.STATUS') }}
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.INBOX') }}
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.MESSAGE') }}
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DISPATCHED_AT') }}
            </th>
            <th class="px-3 py-2.5 text-left font-semibold">
              {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.ERROR') }}
            </th>
            <th class="px-3 py-2.5 text-right font-semibold" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="log in logs"
            :key="log.id"
            class="border-b border-n-slate-4 transition-colors last:border-0 hover:bg-n-alpha-black2"
          >
            <td class="px-3 py-2.5 align-top">
              <input
                type="checkbox"
                class="mt-0.5 h-4 w-4 cursor-pointer rounded border-n-slate-6"
                :checked="selectAllMatching || selectedLogIds.includes(log.id)"
                @change="toggleLogSelection(log.id)"
              />
            </td>
            <td class="px-3 py-2.5 font-medium text-n-slate-12">
              <div>{{ log.contact_name || '-' }}</div>
              <p
                v-if="log.source_groups?.length"
                class="mt-1 text-xs font-normal text-n-slate-10"
              >
                {{ formatSourceSummary(log) }}
              </p>
            </td>
            <td class="px-3 py-2.5 tabular-nums text-n-slate-11">
              {{ log.contact_phone || '-' }}
            </td>
            <td class="px-3 py-2.5 tabular-nums text-n-slate-10">
              {{ log.message_index + 1 }}
            </td>
            <td class="px-3 py-2.5">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                :class="
                  statusColorMap[log.status] || 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{ rowStatusLabel(log) }}
              </span>
            </td>
            <td class="px-3 py-2.5 text-n-slate-11">
              {{ log.dispatch_inbox_name || '-' }}
            </td>
            <td class="max-w-[220px] truncate px-3 py-2.5 text-n-slate-11">
              {{ log.content_sent || log.file_name || '-' }}
            </td>
            <td class="px-3 py-2.5 tabular-nums text-n-slate-11">
              {{ formatDate(log.dispatched_at) }}
            </td>
            <td class="max-w-[150px] truncate px-3 py-2.5 text-n-ruby-9">
              {{ log.error_message || '' }}
            </td>
            <td class="px-3 py-2.5">
              <div class="flex items-center justify-end gap-0.5">
                <Button
                  v-if="hasWhatsapp(log)"
                  variant="ghost"
                  size="xs"
                  color="teal"
                  type="button"
                  icon="i-lucide-message-circle"
                  title="Abrir WhatsApp (sem mensagem)"
                  aria-label="Abrir WhatsApp sem mensagem"
                  @click="openWhatsapp(log, false)"
                />
                <Button
                  v-if="hasWhatsapp(log)"
                  variant="ghost"
                  size="xs"
                  color="teal"
                  type="button"
                  icon="i-lucide-message-square-text"
                  title="Abrir WhatsApp com a mensagem"
                  aria-label="Abrir WhatsApp com a mensagem"
                  @click="openWhatsapp(log, true)"
                />
                <Button
                  v-if="canMarkDispatched(log)"
                  variant="ghost"
                  size="xs"
                  color="teal"
                  type="button"
                  icon="i-lucide-check-check"
                  title="Marcar como concluído (não dispara de novo)"
                  aria-label="Marcar como concluído"
                  :is-loading="markingDispatchedIds.has(log.id)"
                  @click="handleMarkDispatched(log)"
                />
                <Button
                  variant="ghost"
                  size="xs"
                  color="slate"
                  type="button"
                  icon="i-lucide-eye"
                  :title="t('CAMPAIGN.ENHANCED.BUTTONS.DETAIL')"
                  :aria-label="t('CAMPAIGN.ENHANCED.BUTTONS.DETAIL')"
                  @click="openDetails(log)"
                />
                <Button
                  v-if="canRedispatch(log)"
                  variant="ghost"
                  size="xs"
                  color="blue"
                  type="button"
                  icon="i-lucide-send"
                  :title="t('CAMPAIGN.ENHANCED.BUTTONS.REDISPATCH')"
                  :aria-label="t('CAMPAIGN.ENHANCED.BUTTONS.REDISPATCH')"
                  :is-loading="redispatchingIds.has(log.id)"
                  @click="handleRedispatch(log)"
                />
                <Button
                  v-if="canRemoveContactDispatch(log)"
                  variant="ghost"
                  size="xs"
                  color="ruby"
                  type="button"
                  icon="i-lucide-user-x"
                  :title="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_CONTACT')"
                  :aria-label="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_CONTACT')"
                  :is-loading="removingContactIds.has(log.id)"
                  @click="requestRemoveContact(log)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="meta.total_pages > 1"
      class="flex items-center justify-center gap-3 mt-4"
    >
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-chevron-left"
        :disabled="meta.page <= 1 || isLoading"
        @click="fetchLogs(meta.page - 1)"
      />
      <span class="text-xs text-n-slate-11">
        Página {{ meta.page }} / {{ meta.total_pages }}
      </span>
      <Button
        variant="outline"
        size="sm"
        icon="i-lucide-chevron-right"
        :disabled="meta.page >= meta.total_pages || isLoading"
        @click="fetchLogs(meta.page + 1)"
      />
    </div>

    <Dialog
      ref="removeDialogRef"
      width="md"
      type="alert"
      :title="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_CONTACT')"
      :description="t('CAMPAIGN.ENHANCED.DISPATCH_LOG.REMOVE_CONTACT_CONFIRM')"
      :confirm-button-label="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_CONTACT')"
      :cancel-button-label="t('CAMPAIGN.ENHANCED.BUTTONS.CANCEL')"
      :is-loading="removeDialogLoading"
      @close="closeRemoveDialog"
      @confirm="handleRemoveContact"
    >
      <p class="text-sm text-n-slate-12 whitespace-pre-wrap break-words">
        {{
          pendingRemovalLog?.contact_name ||
          pendingRemovalLog?.contact_phone ||
          '-'
        }}
      </p>
    </Dialog>

    <Dialog
      ref="detailDialogRef"
      width="2xl"
      overflow-y-auto
      :title="t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_DIALOG.TITLE')"
      :description="
        t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_DIALOG.DESCRIPTION')
      "
      :show-confirm-button="false"
      @close="closeDetails"
    >
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button
            variant="faded"
            color="slate"
            :label="t('CAMPAIGN.ENHANCED.BUTTONS.CANCEL')"
            type="button"
            @click="closeDetails"
          />
          <Button
            v-if="selectedLog && canRemoveContactDispatch(selectedLog)"
            variant="outline"
            color="ruby"
            :label="t('CAMPAIGN.ENHANCED.BUTTONS.REMOVE_CONTACT')"
            type="button"
            @click="requestRemoveContact(selectedLog)"
          />
        </div>
      </template>

      <div
        v-if="selectedLog"
        class="max-h-[70vh] space-y-4 overflow-y-auto pr-2"
      >
        <div class="grid gap-3 md:grid-cols-2">
          <div
            v-for="item in detailItems"
            :key="item.label"
            class="rounded-lg border border-n-slate-6 bg-n-alpha-black2 p-3"
          >
            <p
              class="mb-1 text-xs font-medium uppercase tracking-wide text-n-slate-11"
            >
              {{ item.label }}
            </p>
            <p class="text-sm whitespace-pre-wrap break-words text-n-slate-12">
              {{ formatValue(item.value) }}
            </p>
          </div>
        </div>

        <div class="rounded-lg border border-n-slate-6 bg-n-alpha-black2 p-3">
          <p
            class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.API_RESPONSE') }}
          </p>
          <pre
            class="overflow-x-auto whitespace-pre-wrap break-words text-xs text-n-slate-12"
          >
            {{ formatJson(selectedLog.api_response) }}
          </pre>
        </div>

        <div class="rounded-lg border border-n-slate-6 bg-n-alpha-black2 p-3">
          <p
            class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ t('CAMPAIGN.ENHANCED.DISPATCH_LOG.DETAIL_FIELDS.METADATA') }}
          </p>
          <pre
            class="overflow-x-auto whitespace-pre-wrap break-words text-xs text-n-slate-12"
          >
            {{ formatJson(selectedMetadata) }}
          </pre>
        </div>
      </div>
    </Dialog>
  </div>
</template>
